/**
 * Reiter „Spaltenzuordnungen“: Wird ein ERP-Export (z. B. SAP-Rechnungsliste) analysiert, schlägt der
 * Server vor, welche Spalte welches Rechnungsfeld ist. Gelesen wird der Export erst, wenn hier jemand
 * die Zuordnung bestätigt hat; jeder spätere Export mit derselben Kopfzeile läuft dann ohne Rückfrage
 * (tri-server docs/fm-baselining-entwurf.md 6.3).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { CheckCircle2, RefreshCw } from "lucide-react";

import { authenticationAtom } from "../../State";
import { useBaseliningAPI, type ColumnMapping, type MappableField } from "../../BaseliningAPI";
import { NONE, columnsFromSelection, doubledFields, fieldByColumn, missingRequired } from "./mappingModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";

export default function ColumnMappingsTab() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer ?? "";
  const api = useBaseliningAPI(customer);

  const [mappings, setMappings] = useState<ColumnMapping[] | null>(null);
  const [fields, setFields] = useState<MappableField[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (!customer) return;
    setError(undefined);
    try {
      const result = await api.listColumnMappings();
      setMappings(result.mappings);
      setFields(result.fields);
    } catch (e: any) {
      setError(e?.message ?? t("baselining.mappings.loadFailed"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!mappings) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">{t("baselining.mappings.intro")}</p>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label={t("baselining.catalogue.reload")}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      {mappings.length === 0 ? (
        <Alert>
          <AlertDescription>{t("baselining.mappings.empty")}</AlertDescription>
        </Alert>
      ) : (
        mappings.map((m) => <MappingCard key={m.id} mapping={m} fields={fields} onConfirmed={load} />)
      )}
    </div>
  );
}

function MappingCard({ mapping, fields, onConfirmed }: { mapping: ColumnMapping; fields: MappableField[]; onConfirmed: () => void }) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useBaseliningAPI(authentication?.customer ?? "");
  const header = useMemo(() => mapping.header.filter((h) => h && h.trim()), [mapping.header]);
  const [selection, setSelection] = useState<Record<string, string>>(() => fieldByColumn(mapping.columns));
  const [saving, setSaving] = useState(false);

  const columns = columnsFromSelection(selection);
  const missing = missingRequired(columns);
  const doubled = doubledFields(selection);
  const confirmed = mapping.source === "confirmed";

  const confirm = async () => {
    setSaving(true);
    try {
      await api.confirmColumnMapping(mapping.id, columns);
      toast.success(t("baselining.mappings.confirmed"));
      onConfirmed();
    } catch (e: any) {
      toast.error(e?.message ?? t("baselining.mappings.confirmFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-base">{mapping.sourceName ?? t("baselining.mappings.untitled")}</CardTitle>
        {confirmed ? (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("baselining.mappings.statusConfirmed")}
          </Badge>
        ) : (
          <Badge variant="outline">{t("baselining.mappings.statusProposed")}</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-2 font-normal">{t("baselining.mappings.column")}</th>
              <th className="py-1 font-normal">{t("baselining.mappings.field")}</th>
            </tr>
          </thead>
          <tbody>
            {header.map((column) => (
              <tr key={column} className="border-t">
                <td className="py-1 pr-2 font-mono text-xs">{column}</td>
                <td className="py-1">
                  <Select
                    value={selection[column] ?? NONE}
                    onValueChange={(value) => setSelection((s) => ({ ...s, [column]: value }))}
                  >
                    <SelectTrigger className="h-8 w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t("baselining.mappings.none")}</SelectItem>
                      {fields.map((f) => (
                        <SelectItem key={f.field} value={f.field}>
                          {t(`baselining.fields.${f.field}`, f.field)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {missing.length > 0 && (
          <Alert>
            <AlertDescription>
              {t("baselining.mappings.missing", { fields: missing.map((m) => t(`baselining.mappings.required.${m}`)).join(", ") })}
            </AlertDescription>
          </Alert>
        )}
        {doubled.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              {t("baselining.mappings.doubled", { fields: doubled.map((f) => t(`baselining.fields.${f}`, f)).join(", ") })}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {confirmed && mapping.confirmedBy
              ? t("baselining.mappings.confirmedBy", { who: mapping.confirmedBy })
              : t("baselining.mappings.afterConfirm")}
          </p>
          <Button onClick={() => void confirm()} disabled={saving || missing.length > 0 || doubled.length > 0}>
            {confirmed ? t("baselining.mappings.saveAgain") : t("baselining.mappings.confirm")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
